import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { notificationsApi } from "../../services/notificationsApi";

function fmtDateTime(value) {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleString("sr-RS");
}

function getPriorityBadgeClass(priority) {
  switch (priority) {
    case "urgent":
      return "bg-red-50 text-red-700 border-red-200 dark:bg-red-950/30 dark:text-red-300 dark:border-red-900";
    case "high":
      return "bg-orange-50 text-orange-700 border-orange-200 dark:bg-orange-950/30 dark:text-orange-300 dark:border-orange-900";
    case "low":
      return "bg-gray-100 text-gray-700 border-gray-200 dark:bg-neutral-800 dark:text-neutral-300 dark:border-neutral-700";
    default:
      return "bg-slate-100 text-slate-700 border-slate-200 dark:bg-slate-900/40 dark:text-slate-300 dark:border-slate-800";
  }
}

export default function NotificationBell() {
  const navigate = useNavigate();
  const wrapRef = useRef(null);

  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);

  async function loadUnreadCount() {
    try {
      const res = await notificationsApi.getUnreadCount();
      setUnreadCount(res.data?.unreadCount || 0);
    } catch (err) {
      console.error("Failed to load unread count:", err);
    }
  }

  async function loadNotifications() {
    try {
      setLoading(true);
      const res = await notificationsApi.getAll({ limit: 10 });
      setItems(res.data?.items || []);
    } catch (err) {
      console.error("Failed to load notifications:", err);
    } finally {
      setLoading(false);
    }
  }

  async function handleOpen() {
    const nextOpen = !open;
    setOpen(nextOpen);

    if (nextOpen) {
      await loadNotifications();
      await loadUnreadCount();
    }
  }

  async function handleNotificationClick(item) {
    try {
      if (!item.is_read) {
        await notificationsApi.markRead(item.id);
      }

      await loadUnreadCount();
      setOpen(false);

      if (item.action_url) {
        navigate(item.action_url);
      }
    } catch (err) {
      console.error("Failed to open notification:", err);
    }
  }

  async function handleMarkAllRead() {
    try {
      await notificationsApi.markAllRead();
      await loadNotifications();
      await loadUnreadCount();
    } catch (err) {
      console.error("Failed to mark all as read:", err);
    }
  }

  useEffect(() => {
    loadUnreadCount();

    const timer = setInterval(() => {
      loadUnreadCount();
    }, 30000);

    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    function handleClickOutside(e) {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setOpen(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
  const handleRefresh = () => {
    loadUnreadCount();

    if (open) {
      loadNotifications();
    }
  };

  window.addEventListener("notifications:refresh", handleRefresh);

  return () => {
    window.removeEventListener("notifications:refresh", handleRefresh);
  };
}, [open]);

useEffect(() => {
  const handleFocus = () => {
    loadUnreadCount();

    if (open) {
      loadNotifications();
    }
  };

  const handleVisibility = () => {
    if (document.visibilityState === "visible") {
      loadUnreadCount();

      if (open) {
        loadNotifications();
      }
    }
  };

  window.addEventListener("focus", handleFocus);
  document.addEventListener("visibilitychange", handleVisibility);

  return () => {
    window.removeEventListener("focus", handleFocus);
    document.removeEventListener("visibilitychange", handleVisibility);
  };
}, [open]);

  return (
    <div className="relative" ref={wrapRef}>
      <button
        onClick={handleOpen}
        className="relative px-3 py-2 rounded-xl border border-gray-300 dark:border-neutral-700 bg-white dark:bg-neutral-900 text-black dark:text-white"
        aria-label="Notifications"
      >
        <span className="text-lg">🔔</span>

        {unreadCount > 0 && (
          <span className="absolute -top-2 -right-2 min-w-[20px] h-5 px-1 rounded-full bg-red-600 text-white text-[11px] flex items-center justify-center">
            {unreadCount > 99 ? "99+" : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 mt-3 w-[380px] max-w-[90vw] bg-white dark:bg-neutral-900 border border-gray-200 dark:border-neutral-700 rounded-2xl shadow-xl z-50 overflow-hidden">
          <div className="px-4 py-2 border-b border-gray-200 dark:border-neutral-700 flex items-center justify-between">
            <div>
              <div className="font-semibold text-black dark:text-white">
                Notifications
              </div>
              <div className="text-xs text-gray-500 dark:text-neutral-400">
                Unread: {unreadCount}
              </div>
            </div>

            <button
              onClick={handleMarkAllRead}
              className="text-sm px-3 py-1 rounded-lg border border-gray-300 dark:border-neutral-700 text-black dark:text-white"
            >
              Mark all read
            </button>
          </div>

          <div className="max-h-[420px] overflow-y-auto">
            {loading ? (
              <div className="p-4 text-sm text-gray-500 dark:text-neutral-400">
                Loading...
              </div>
            ) : items.length === 0 ? (
              <div className="p-4 text-sm text-gray-500 dark:text-neutral-400">
                No notifications.
              </div>
            ) : (
              <div className="divide-y divide-gray-200 dark:divide-neutral-700">
                {items.map((item) => (
                  <button
                    key={item.id}
                    onClick={() => handleNotificationClick(item)}
                    className={`w-full text-left px-4 py-3 hover:bg-gray-50 dark:hover:bg-neutral-800 transition ${
                      item.is_read ? "" : "bg-blue-50/40 dark:bg-blue-950/10"
                    }`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="font-medium text-black dark:text-white">
                          {item.title}
                        </div>
                        {item.message ? (
                          <div className="text-sm text-gray-600 dark:text-neutral-300 mt-1">
                            {item.message}
                          </div>
                        ) : null}
                        <div className="text-xs text-gray-500 dark:text-neutral-400 mt-2">
                          {fmtDateTime(item.created_at)}
                        </div>
                      </div>

                      <div className="flex flex-col items-end gap-2 shrink-0">
                        <span
                          className={`px-2 py-1 rounded-full border text-[11px] ${getPriorityBadgeClass(item.priority)}`}
                        >
                          {item.priority || "normal"}
                        </span>

                        {!item.is_read && (
                          <span className="w-2 h-2 rounded-full bg-blue-600" />
                        )}
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}